import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import {
  Users,
  Search,
  Wallet,
  Eye,
  Edit,
  Calendar,
  Download,
  User,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Ticket,
  Send,
  Trash2,
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Tooltip,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Checkbox,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Commuter {
  id: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  role: string
  status: 'active' | 'inactive' | 'suspended'
  walletBalance: number
  kycStatus: 'pending' | 'verified' | 'rejected'
  createdAt: string
  updatedAt: string
  totalBookings?: number
  lastBooking?: string
  totalSpent?: number
}

interface CleanupEmailAttempt {
  id: string
  recipient: string
  status: string
  subject: string
  message: string
  fullName: string
  deletedUserId?: string | null
  error?: string | null
  createdAt: string
  updatedAt: string
}

const PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS = 3

function isPendingKycCleanupEligible(user: Commuter, minimumAgeHours = PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS) {
  if (user.kycStatus !== 'pending') {
    return false
  }

  const createdAt = new Date(user.createdAt).getTime()
  return Date.now() - createdAt >= minimumAgeHours * 60 * 60 * 1000
}

// ─── View Modal ───────────────────────────────────────────────────────────────

function CommuterViewModal({
  isOpen,
  onClose,
  commuter,
  isCommuter,
}: {
  isOpen: boolean
  onClose: () => void
  commuter: Commuter | null
  isCommuter: boolean
}) {
  if (!commuter) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg={isCommuter ? 'purple.100' : 'blue.100'} borderRadius="lg">
              <User size={20} color={isCommuter ? 'var(--chakra-colors-purple-600)' : 'var(--chakra-colors-blue-600)'} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">
                {commuter.firstName} {commuter.lastName}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {isCommuter ? 'Commuter Details' : 'Registered User Details'}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Full Name</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {commuter.firstName} {commuter.lastName}
                    </Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Account Status</Text>
                    <Badge
                      colorScheme={commuter.status === 'active' ? 'green' : commuter.status === 'inactive' ? 'gray' : 'red'}
                      variant="subtle" borderRadius="full" px={3} py={1} textTransform="capitalize"
                    >
                      {commuter.status}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Mail size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Email</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="semibold" color="gray.900">{commuter.email || '—'}</Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Phone size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Phone</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="semibold" color="gray.900">{commuter.phoneNumber || '—'}</Text>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Wallet size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Wallet</Text>
                    </HStack>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      TZS {Number(commuter.walletBalance ?? 0).toLocaleString()}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Ticket size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Total Bookings</Text>
                    </HStack>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {commuter.totalBookings ?? 0}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">KYC Status</Text>
                    <Badge
                      colorScheme={commuter.kycStatus === 'verified' ? 'green' : commuter.kycStatus === 'pending' ? 'yellow' : 'red'}
                      variant="subtle" borderRadius="full" px={3} py={1} textTransform="capitalize"
                    >
                      {commuter.kycStatus}
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Calendar size={16} color="var(--chakra-colors-gray-600)" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Member Since</Text>
                  </HStack>
                  <Text fontSize="md" fontWeight="semibold" color="gray.900">
                    {new Date(commuter.createdAt).toLocaleDateString()}
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            <HStack spacing={3} w="full" pt={2}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">Close</Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function CommuterEditModal({
  isOpen,
  onClose,
  commuter,
}: {
  isOpen: boolean
  onClose: () => void
  commuter: Commuter | null
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  React.useEffect(() => {
    if (commuter) {
      setFormData({
        firstName: commuter.firstName || '',
        lastName: commuter.lastName || '',
        email: commuter.email || '',
        phoneNumber: commuter.phoneNumber || '',
        status: commuter.status || 'active',
      })
    }
  }, [commuter])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await cmsService.updatePassenger(commuter!.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      })
      toast({ title: 'User Updated', description: `${formData.firstName} ${formData.lastName} has been updated.`, status: 'success', duration: 3000, isClosable: true })
      queryClient.invalidateQueries('passengers')
      onClose()
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Failed to update user.', status: 'error', duration: 3000, isClosable: true })
    } finally {
      setIsLoading(false)
    }
  }

  if (!commuter) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <Edit size={20} color="var(--chakra-colors-blue-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Edit User</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">First Name</FormLabel>
                  <Input value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} placeholder="First Name" borderRadius="xl" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Last Name</FormLabel>
                  <Input value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} placeholder="Last Name" borderRadius="xl" />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Email</FormLabel>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" borderRadius="xl" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Phone Number</FormLabel>
                <Input value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} placeholder="+255 XXX XXX XXX" borderRadius="xl" />
              </FormControl>
              <HStack spacing={3} w="full" pt={4}>
                <Button type="button" variant="outline" onClick={onClose} flex={1} borderRadius="xl">Cancel</Button>
                <Button type="submit" colorScheme="brand" flex={1} borderRadius="xl" isLoading={isLoading} loadingText="Updating..." leftIcon={<Edit size={16} />}>
                  Update User
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// ─── Send Email Modal ─────────────────────────────────────────────────────────

function SendEmailModal({
  isOpen,
  onClose,
  commuters,
}: {
  isOpen: boolean
  onClose: () => void
  commuters: Commuter[]
}) {
  const [subject, setSubject] = useState('Complete Your KYC Verification')
  const [message, setMessage] = useState(
    `Dear User,\n\nWe noticed that your account verification (KYC) is still pending. To fully unlock all features of DalalaSmart, please complete your verification.\n\nBest regards,\nDalalaSmart Team`
  )
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()

  React.useEffect(() => {
    if (commuters.length === 1) {
      setMessage(
        `Dear ${commuters[0].firstName || 'User'},\n\nWe noticed that your account verification (KYC) is still pending. To fully unlock all features of DalalaSmart, please complete your verification.\n\nBest regards,\nDalalaSmart Team`
      )
    } else if (commuters.length > 1) {
      setMessage(
        `Dear User,\n\nWe noticed that your account verification (KYC) is still pending. To fully unlock all features of DalalaSmart, please complete your verification.\n\nBest regards,\nDalalaSmart Team`
      )
    }
  }, [commuters])

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast({ title: 'Missing Fields', description: 'Please provide both subject and message.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    const validCommuters = commuters.filter(c => c.email)
    if (validCommuters.length === 0) {
      toast({ title: 'No Email', description: 'Selected users do not have email addresses.', status: 'error', duration: 3000, isClosable: true })
      return
    }

    setIsLoading(true)
    try {
      const results = await Promise.allSettled(
        validCommuters.map((c) =>
          cmsService.sendEmailToUser(c.id, { subject, message, email: c.email })
        )
      )
      
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      if (failed === 0) {
        toast({ title: 'Emails Sent', description: `Successfully dispatched to all ${successful} users.`, status: 'success', duration: 5000, isClosable: true })
      } else if (successful > 0) {
        toast({ title: 'Partial Success', description: `Sent to ${successful} users, but ${failed} failed.`, status: 'warning', duration: 5000, isClosable: true })
      } else {
        toast({ title: 'Delivery Failed', description: `Failed to send emails to all ${failed} targets.`, status: 'error', duration: 5000, isClosable: true })
      }
      onClose()
    } catch (err) {
      toast({ title: 'System Error', description: 'Encountered a critical error while dispatching emails.', status: 'error', duration: 5000, isClosable: true })
      onClose()
    } finally {
      setIsLoading(false)
    }
  }

  if (commuters.length === 0) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="orange.100" borderRadius="lg">
              <Send size={20} color="var(--chakra-colors-orange-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">Send Email</Text>
              <Text fontSize="sm" color="gray.600">
                To: {commuters.length === 1 ? commuters[0].email || 'No email' : `${commuters.length} users`}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium">Subject</FormLabel>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} borderRadius="xl" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium">Message</FormLabel>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} borderRadius="xl" resize="vertical" />
            </FormControl>
            <HStack spacing={3} w="full" pt={2}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">Cancel</Button>
              <Button colorScheme="orange" flex={1} borderRadius="xl" isLoading={isLoading} loadingText="Sending..." leftIcon={<Send size={16} />} onClick={handleSend}>
                Send Email
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function PendingKycDeleteModal({
  isOpen,
  onClose,
  selectedUsers,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean
  onClose: () => void
  selectedUsers: Commuter[]
  onConfirm: (payload: { forceDelete: boolean; minimumAgeHours: number }) => Promise<void>
  isLoading: boolean
}) {
  const minimumAgeHours = 3
  const [forceDelete, setForceDelete] = useState(false)

  React.useEffect(() => {
    if (!isOpen) return
    setForceDelete(false)
  }, [isOpen])

  const staleUsers = selectedUsers.filter((user) => {
    const createdAt = new Date(user.createdAt).getTime()
    return Date.now() - createdAt >= minimumAgeHours * 60 * 60 * 1000
  })
  const freshUsers = selectedUsers.length - staleUsers.length

  const handleConfirm = async () => {
    await onConfirm({ forceDelete, minimumAgeHours })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>Delete Pending KYC Accounts</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="xl">
              <AlertIcon />
              {selectedUsers.length} pending KYC account(s) will be permanently deleted. No email will be sent.
            </Alert>
            <Text fontSize="sm" color="gray.600">
              By default, only pending KYC accounts older than {minimumAgeHours} hours are eligible for cleanup.
            </Text>
            <Box borderWidth="1px" borderColor="orange.100" bg="orange.50" borderRadius="xl" p={4}>
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" color="orange.800">
                  Selected accounts summary
                </Text>
                <Text fontSize="sm" color="orange.900">
                  Eligible now: {staleUsers.length}
                </Text>
                <Text fontSize="sm" color="orange.900">
                  Too recent: {freshUsers}
                </Text>
              </VStack>
            </Box>
            <Checkbox
              isChecked={forceDelete}
              onChange={(e) => setForceDelete(e.target.checked)}
              colorScheme="red"
            >
              Ignore the {minimumAgeHours}-hour safeguard and permanently delete all selected pending KYC accounts
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={onClose} borderRadius="xl">Cancel</Button>
          <Button colorScheme="red" onClick={handleConfirm} isLoading={isLoading} loadingText="Deleting..." borderRadius="xl">
            Delete Permanently
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function FailedCleanupEmailsModal({
  isOpen,
  onClose,
  attempts,
  isLoading,
  onRefresh,
  onResend,
}: {
  isOpen: boolean
  onClose: () => void
  attempts: CleanupEmailAttempt[]
  isLoading: boolean
  onRefresh: () => void
  onResend: (notificationIds: string[], payload: { emailSubject: string; emailMessage: string }) => Promise<void>
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [subject, setSubject] = useState('Please Re-Register Your Daladala Smart Account')
  const [message, setMessage] = useState('')
  const toast = useToast()

  React.useEffect(() => {
    if (!isOpen) return
    setSelectedIds([])
    setSubject('Please Re-Register Your Daladala Smart Account')
    setMessage([
      'Hello {{name}},',
      '',
      'Your previous Daladala Smart account was removed because the registration remained incomplete at the pending KYC stage.',
      'Please create a fresh account and complete registration again so you can continue using the service.',
      '',
      'If you believe this was a mistake, please contact support.',
      '',
      'Daladala Smart Team',
    ].join('\n'))
  }, [isOpen])

  const toggleSelection = (notificationId: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? Array.from(new Set([...current, notificationId])) : current.filter((id) => id !== notificationId)
    )
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(attempts.map((attempt) => attempt.id))
      return
    }
    setSelectedIds([])
  }

  const handleResend = async () => {
    if (selectedIds.length === 0) {
      toast({
        title: 'No Failed Emails Selected',
        description: 'Select at least one failed email to resend.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    await onResend(selectedIds, {
      emailSubject: subject,
      emailMessage: message,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '4xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>Failed Re-Registration Emails</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <Text color="gray.600" fontSize="sm">
                These are deleted pending-KYC accounts whose re-registration email previously failed.
              </Text>
              <Button size="sm" variant="outline" onClick={onRefresh}>
                Refresh
              </Button>
            </HStack>

            {attempts.length === 0 ? (
              <Alert status="success" borderRadius="xl">
                <AlertIcon />
                No failed re-registration emails are waiting for resend.
              </Alert>
            ) : (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th w="48px">
                            <Checkbox
                              isChecked={selectedIds.length > 0 && selectedIds.length === attempts.length}
                              isIndeterminate={selectedIds.length > 0 && selectedIds.length < attempts.length}
                              onChange={(e) => toggleAll(e.target.checked)}
                              colorScheme="orange"
                            />
                          </Th>
                          <Th>Recipient</Th>
                          <Th>Name</Th>
                          <Th>Error</Th>
                          <Th>Failed At</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {attempts.map((attempt) => (
                          <Tr key={attempt.id}>
                            <Td>
                              <Checkbox
                                isChecked={selectedIds.includes(attempt.id)}
                                onChange={(e) => toggleSelection(attempt.id, e.target.checked)}
                                colorScheme="orange"
                              />
                            </Td>
                            <Td>{attempt.recipient}</Td>
                            <Td>{attempt.fullName || 'User'}</Td>
                            <Td color="red.500">{attempt.error || 'Unknown error'}</Td>
                            <Td>{new Date(attempt.createdAt).toLocaleString()}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </CardBody>
              </Card>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium">Resend Subject</FormLabel>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} borderRadius="xl" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium">Resend Message</FormLabel>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} borderRadius="xl" resize="vertical" />
            </FormControl>
            <Text fontSize="sm" color="gray.500">
              Supported placeholders: <strong>{'{{name}}'}</strong>, <strong>{'{{firstName}}'}</strong>, <strong>{'{{email}}'}</strong>.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={onClose} borderRadius="xl">Close</Button>
          <Button colorScheme="orange" onClick={handleResend} isLoading={isLoading} loadingText="Resending..." borderRadius="xl" isDisabled={attempts.length === 0}>
            Resend Selected
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ─── Shared Table ─────────────────────────────────────────────────────────────

function UserTable({
  users,
  isCommuter,
  onView,
  onEdit,
  onEmail,
  onStatusUpdate,
  searchTerm,
  setSearchTerm,
  selectedStatus,
  setSelectedStatus,
  selectedKycStatus,
  setSelectedKycStatus,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  paidOnly,
  setPaidOnly,
  minBookings,
  setMinBookings,
  dateRange,
  setDateRange,
  selectedPendingIds,
  onTogglePendingSelection,
  onToggleAllPendingSelection,
  showForceDeleteCandidates,
  onShowForceDeleteCandidatesChange,
}: {
  users: Commuter[]
  isCommuter: boolean
  onView: (u: Commuter) => void
  onEdit: (u: Commuter) => void
  onEmail: (u: Commuter) => void
  onStatusUpdate: (id: string, s: 'active' | 'inactive' | 'suspended') => void
  searchTerm: string
  setSearchTerm: (v: string) => void
  selectedStatus: string
  setSelectedStatus: (v: string) => void
  selectedKycStatus: string
  setSelectedKycStatus: (v: string) => void
  currentPage: number
  setCurrentPage: (v: number) => void
  itemsPerPage: number
  paidOnly: boolean
  setPaidOnly: (v: boolean) => void
  minBookings: string
  setMinBookings: (v: string) => void
  dateRange: string
  setDateRange: (v: string) => void
  selectedPendingIds: string[]
  onTogglePendingSelection: (id: string, checked: boolean) => void
  onToggleAllPendingSelection: (ids: string[], checked: boolean) => void
  showForceDeleteCandidates: boolean
  onShowForceDeleteCandidatesChange: (value: boolean) => void
}) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green'
      case 'inactive': return 'gray'
      case 'suspended': return 'red'
      default: return 'gray'
    }
  }

  const getKycStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'green'
      case 'pending': return 'orange'
      case 'rejected': return 'red'
      default: return 'gray'
    }
  }

  const filtered = users.filter((u) => {
    const fullName = `${u?.firstName || ''} ${u?.lastName || ''}`.toLowerCase()
    const email = u?.email?.toLowerCase() || ''
    const phone = u?.phoneNumber?.toLowerCase() || ''
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      email.includes(searchTerm.toLowerCase()) ||
      phone.includes(searchTerm.toLowerCase())
    const matchesStatus = selectedStatus === 'all' || u?.status === selectedStatus
    const matchesKyc = selectedKycStatus === 'all' || u?.kycStatus === selectedKycStatus
    
    // Advanced filters
    const matchesPaid = !paidOnly || (u.totalSpent ?? 0) > 0
    const matchesMinBookings = minBookings === '' || (u.totalBookings ?? 0) >= Number(minBookings)
    
    let matchesDate = true
    if (dateRange !== 'all') {
      const joinedDate = new Date(u.createdAt)
      const now = new Date()
      if (dateRange === 'last-week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        matchesDate = joinedDate >= weekAgo
      } else if (dateRange === 'last-month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        matchesDate = joinedDate >= monthAgo
      } else if (dateRange === 'last-3-months') {
        const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
        matchesDate = joinedDate >= quarterAgo
      } else if (dateRange === 'older-than-3-months') {
        const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
        matchesDate = joinedDate < quarterAgo
      }
    }

    return matchesSearch && matchesStatus && matchesKyc && matchesPaid && matchesMinBookings && matchesDate
  })

  const totalPages = Math.ceil(filtered.length / itemsPerPage)
  const start = (currentPage - 1) * itemsPerPage
  const current = filtered.slice(start, start + itemsPerPage)
  const selectablePendingIds = filtered
    .filter((u) => !isCommuter && u.kycStatus === 'pending')
    .filter((u) => showForceDeleteCandidates || isPendingKycCleanupEligible(u))
    .map((u) => u.id)
  const stalePendingInView = filtered.filter((u) => !isCommuter && isPendingKycCleanupEligible(u)).length
  const freshPendingInView = filtered.filter((u) => !isCommuter && u.kycStatus === 'pending' && !isPendingKycCleanupEligible(u)).length
  const selectedPendingCount = selectablePendingIds.filter((id) => selectedPendingIds.includes(id)).length
  const allPendingSelected = selectablePendingIds.length > 0 && selectedPendingCount === selectablePendingIds.length
  const somePendingSelected = selectedPendingCount > 0 && selectedPendingCount < selectablePendingIds.length

  const label = isCommuter ? 'commuter' : 'user'

  return (
    <VStack spacing={4} align="stretch">
      {/* Filters */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <VStack spacing={4} align="stretch" w="full">
            <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }} w="full">
              <InputGroup maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                  placeholder={`Search ${label}s by name, email, or phone...`}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }}
                  borderRadius="xl"
                />
              </InputGroup>
              <Select maxW="180px" value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1) }} borderRadius="xl">
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
              <Select maxW="180px" value={selectedKycStatus} onChange={(e) => { setSelectedKycStatus(e.target.value); setCurrentPage(1) }} borderRadius="xl">
                <option value="all">All KYC</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </Select>
            </HStack>

            <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }} w="full">
              <Select 
                maxW="200px" 
                value={paidOnly ? 'paid' : 'all'} 
                onChange={(e) => { setPaidOnly(e.target.value === 'paid'); setCurrentPage(1) }}
                borderRadius="xl"
              >
                <option value="all">All Payment History</option>
                <option value="paid">Has Paid Bookings</option>
              </Select>

              <HStack spacing={2} maxW="180px">
                <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Min Bookings:</Text>
                <Input
                  type="number"
                  placeholder="0"
                  value={minBookings}
                  onChange={(e) => { setMinBookings(e.target.value); setCurrentPage(1) }}
                  borderRadius="xl"
                  size="sm"
                />
              </HStack>

              <Select 
                maxW="220px" 
                value={dateRange} 
                onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1) }}
                borderRadius="xl"
              >
                <option value="all">Any Joining Date</option>
                <option value="last-week">Joined Last 7 Days</option>
                <option value="last-month">Joined Last 30 Days</option>
                <option value="last-3-months">Joined Last 90 Days</option>
                <option value="older-than-3-months">Joined &gt; 90 Days Ago</option>
              </Select>

              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStatus('all');
                  setSelectedKycStatus('all');
                  setPaidOnly(false);
                  setMinBookings('');
                  setDateRange('all');
                }}
              >
                Reset Filters
              </Button>
            </HStack>
            {!isCommuter && (
              <HStack
                spacing={4}
                flexDirection={{ base: 'column', md: 'row' }}
                align={{ base: 'start', md: 'center' }}
                w="full"
              >
                <Checkbox
                  isChecked={showForceDeleteCandidates}
                  onChange={(e) => onShowForceDeleteCandidatesChange(e.target.checked)}
                  colorScheme="red"
                >
                  Include fresh pending accounts for force delete
                </Checkbox>
                <Text fontSize="sm" color="gray.600">
                  Eligible now: {stalePendingInView} older than {PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS} hours
                  {freshPendingInView > 0 ? `, hidden by default: ${freshPendingInView}` : ''}
                </Text>
              </HStack>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">
              {isCommuter ? 'Commuters List' : 'Registered Users List'}
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {filtered.length} {label}{filtered.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  {!isCommuter && (
                    <Th borderBottom="2px solid" borderColor="gray.200" py={4} w="52px">
                      <Checkbox
                        isChecked={allPendingSelected}
                        isIndeterminate={somePendingSelected}
                        onChange={(e) => onToggleAllPendingSelection(selectablePendingIds, e.target.checked)}
                        colorScheme="red"
                      />
                    </Th>
                  )}
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>{isCommuter ? 'Commuter' : 'User'}</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Contact</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Wallet</Th>
                  {isCommuter && <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Bookings</Th>}
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>KYC</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Joined</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {current.length === 0 ? (
                  <Tr>
                    <Td colSpan={isCommuter ? 8 : 8} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Users size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">No {label}s found</Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedStatus !== 'all' || selectedKycStatus !== 'all'
                                ? `No ${label}s match your current filters.`
                                : isCommuter
                                  ? 'No commuters yet — commuters are users who have booked at least one ride.'
                                  : 'No registered users yet.'
                              }
                            </Text>
                          </VStack>
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  current.map((u) => (
                    <Tr key={u.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                      {!isCommuter && (
                        <Td py={4}>
                          {u.kycStatus === 'pending' ? (
                            (() => {
                              const isEligible = isPendingKycCleanupEligible(u)
                              const isSelectable = isEligible || showForceDeleteCandidates
                              return (
                                <Tooltip
                                  label={
                                    isSelectable
                                      ? isEligible
                                        ? 'Eligible for cleanup now'
                                        : 'Fresh pending account included because force-delete mode is enabled'
                                      : `Selectable after ${PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS} hours, or enable force-delete mode`
                                  }
                                >
                                  <Box>
                                    <Checkbox
                                      isChecked={selectedPendingIds.includes(u.id)}
                                      onChange={(e) => onTogglePendingSelection(u.id, e.target.checked)}
                                      colorScheme="red"
                                      isDisabled={!isSelectable}
                                    />
                                  </Box>
                                </Tooltip>
                              )
                            })()
                          ) : null}
                        </Td>
                      )}
                      <Td py={4}>
                        <HStack spacing={3}>
                          <Avatar size="sm" bg={isCommuter ? 'purple.100' : 'blue.100'} color={isCommuter ? 'purple.600' : 'blue.600'} name={`${u.firstName} ${u.lastName}`} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" color="gray.900">{u.firstName} {u.lastName}</Text>
                            <Text fontSize="sm" color="gray.500">{u.email || '—'}</Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        <HStack spacing={2}>
                          <Phone size={12} color="var(--chakra-colors-gray-500)" />
                          <Text fontSize="sm" color="gray.900">{u.phoneNumber || '—'}</Text>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        <HStack spacing={2}>
                          <Wallet size={14} color="var(--chakra-colors-green-500)" />
                          <Text fontSize="sm" color="gray.900" fontWeight="medium">
                            TZS {Number(u?.walletBalance ?? 0).toLocaleString()}
                          </Text>
                        </HStack>
                      </Td>
                      {isCommuter && (
                        <Td py={4}>
                          <HStack spacing={2}>
                            <Ticket size={14} color="var(--chakra-colors-purple-500)" />
                            <Text fontSize="sm" color="gray.900" fontWeight="medium">
                              {u.totalBookings ?? 0}
                            </Text>
                          </HStack>
                        </Td>
                      )}
                      <Td py={4}>
                        <VStack align="start" spacing={1}>
                          <Badge
                            colorScheme={getKycStatusColor(u.kycStatus)}
                            variant="subtle" px={3} py={1} borderRadius="full" textTransform="capitalize"
                          >
                            {u.kycStatus}
                          </Badge>
                          {!isCommuter && u.kycStatus === 'pending' && (
                            <Text fontSize="xs" color={isPendingKycCleanupEligible(u) ? 'green.600' : 'orange.600'}>
                              {isPendingKycCleanupEligible(u)
                                ? 'Cleanup eligible'
                                : `Wait until ${PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS}h old`}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td py={4}>
                        <Badge
                          colorScheme={getStatusColor(u.status)}
                          variant="subtle" px={3} py={1} borderRadius="full" textTransform="capitalize"
                        >
                          {u.status}
                        </Badge>
                      </Td>
                      <Td py={4}>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" color="gray.600">{new Date(u.createdAt).toLocaleDateString()}</Text>
                        </VStack>
                      </Td>
                      <Td py={4}>
                        <HStack spacing={1}>
                          <Tooltip label="View Details">
                            <IconButton aria-label="View" icon={<Eye size={16} />} size="sm" variant="ghost" colorScheme="blue" onClick={() => onView(u)} />
                          </Tooltip>
                          <Tooltip label="Edit User">
                            <IconButton aria-label="Edit" icon={<Edit size={16} />} size="sm" variant="ghost" colorScheme="blue" onClick={() => onEdit(u)} />
                          </Tooltip>
                          {u.kycStatus === 'pending' && (
                            <Tooltip label="Send KYC Email">
                              <IconButton
                                aria-label="Send Email"
                                icon={<Mail size={16} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="orange"
                                onClick={() => onEmail(u)}
                              />
                            </Tooltip>
                          )}
                          <Tooltip label="Update Status">
                            <Select
                              size="sm"
                              value={u.status}
                              onChange={(e) => onStatusUpdate(u.id, e.target.value as any)}
                              w="110px"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="suspended">Suspended</option>
                            </Select>
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>

        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100">
            <Button size="sm" variant="outline" onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))} isDisabled={currentPage === 1}>
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600" px={4}>Page {currentPage} of {totalPages}</Text>
            <Button size="sm" variant="outline" onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))} isDisabled={currentPage === totalPages}>
              Next
            </Button>
          </HStack>
        )}
      </Card>
    </VStack>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PassengersPage() {
  // View modal
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  // Edit modal
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  // Email modal
  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure()
  const { isOpen: isDeletePendingOpen, onOpen: onDeletePendingOpen, onClose: onDeletePendingClose } = useDisclosure()
  const { isOpen: isFailedEmailsOpen, onOpen: onFailedEmailsOpen, onClose: onFailedEmailsClose } = useDisclosure()
  const [emailTargets, setEmailTargets] = useState<Commuter[]>([])

  const [selectedUser, setSelectedUser] = useState<Commuter | null>(null)
  const [isViewingCommuter, setIsViewingCommuter] = useState(true)
  const [selectedPendingIds, setSelectedPendingIds] = useState<string[]>([])
  const [showForceDeleteCandidates, setShowForceDeleteCandidates] = useState(false)
  const [isDeletingPending, setIsDeletingPending] = useState(false)
  const [isResendingFailedEmails, setIsResendingFailedEmails] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Per-tab filter states
  const [commSearchTerm, setCommSearchTerm] = useState('')
  const [commStatus, setCommStatus] = useState('all')
  const [commKyc, setCommKyc] = useState('all')
  const [commPaid, setCommPaid] = useState(false)
  const [commMinBookings, setCommMinBookings] = useState('')
  const [commDateRange, setCommDateRange] = useState('all')
  const [commPage, setCommPage] = useState(1)

  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [userStatus, setUserStatus] = useState('all')
  const [userKyc, setUserKyc] = useState('all')
  const [userPaid, setUserPaid] = useState(false)
  const [userMinBookings, setUserMinBookings] = useState('')
  const [userDateRange, setUserDateRange] = useState('all')
  const [userPage, setUserPage] = useState(1)

  const ITEMS_PER_PAGE = 10

  // Fetch data
  const { data: passengersData, isLoading } = useQuery(
    'passengers',
    () => cmsService.getPassengers({ limit: 10000 }),
    { retry: false }
  )

  const { data: passengerStats } = useQuery(
    'passenger-stats',
    () => cmsService.getPassengerStats(),
    { retry: false }
  )

  const {
    data: failedCleanupEmailsData,
    refetch: refetchFailedCleanupEmails,
    isFetching: isFetchingFailedCleanupEmails,
  } = useQuery(
    'pending-kyc-cleanup-email-attempts',
    () => cmsService.getPendingKycCleanupEmailAttempts('failed'),
    { retry: false }
  )

  const allUsers: Commuter[] = passengersData?.data || []
  const statsData = passengerStats?.data || {}
  const failedCleanupEmails: CleanupEmailAttempt[] = failedCleanupEmailsData?.data || []

  // Commuters: users who have booked at least once
  const commuters = allUsers.filter((u) => (u.totalBookings ?? 0) > 0)
  // Users tab should show all passenger accounts, not only KYC-complete accounts.
  const registeredUsers = allUsers

  const pendingCount = allUsers.filter((u) => u.kycStatus === 'pending').length
  const selectedPendingUsers = registeredUsers.filter((user) => selectedPendingIds.includes(user.id))
  const stalePendingCount = registeredUsers.filter((user) => isPendingKycCleanupEligible(user)).length
  const freshPendingCount = registeredUsers.filter((user) => user.kycStatus === 'pending' && !isPendingKycCleanupEligible(user)).length

  React.useEffect(() => {
    if (showForceDeleteCandidates) {
      return
    }

    setSelectedPendingIds((current) =>
      current.filter((id) => {
        const user = registeredUsers.find((candidate) => candidate.id === id)
        return user ? isPendingKycCleanupEligible(user) : false
      })
    )
  }, [registeredUsers, showForceDeleteCandidates])

  const stats = [
    { title: 'Passenger Accounts', value: statsData.registered || statsData.total || registeredUsers.length, color: 'blue', icon: Users },
    { title: 'Commuters', value: statsData.commuters || commuters.length, color: 'purple', icon: Ticket },
    { title: 'Verified', value: statsData.verified || 0, color: 'green', icon: CheckCircle },
    { title: 'Pending KYC', value: statsData.pendingKyc || pendingCount || 0, color: 'orange', icon: AlertCircle },
    { title: 'Total Wallet Balance', value: statsData.totalWalletBalance || 0, color: 'teal', icon: Wallet },
  ]

  const handleView = (user: Commuter, asCommuter: boolean) => {
    setSelectedUser(user)
    setIsViewingCommuter(asCommuter)
    onViewOpen()
  }

  const handleEdit = (user: Commuter) => {
    setSelectedUser(user)
    onEditOpen()
  }

  const handleEmail = (user: Commuter) => {
    setEmailTargets([user])
    onEmailOpen()
  }

  const handleEmailPending = () => {
    const pendingUsers = allUsers.filter(u => u.kycStatus === 'pending')
    setEmailTargets(pendingUsers)
    onEmailOpen()
  }

  const togglePendingSelection = (userId: string, checked: boolean) => {
    setSelectedPendingIds((current) =>
      checked ? Array.from(new Set([...current, userId])) : current.filter((id) => id !== userId)
    )
  }

  const toggleAllPendingSelection = (userIds: string[], checked: boolean) => {
    if (checked) {
      setSelectedPendingIds((current) => Array.from(new Set([...current, ...userIds])))
      return
    }
    setSelectedPendingIds((current) => current.filter((id) => !userIds.includes(id)))
  }

  const handleDeletePendingKyc = async (payload: { forceDelete: boolean; minimumAgeHours: number }) => {
    if (selectedPendingIds.length === 0) {
      toast({
        title: 'No Pending KYC Accounts Selected',
        description: 'Select at least one pending KYC passenger account first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsDeletingPending(true)
    try {
      const response = await cmsService.cleanupPendingKycPassengers({
        userIds: selectedPendingIds,
        ...payload,
      })
      const deleted = response?.data?.deleted || 0
      const failed = response?.data?.failed || 0

      queryClient.invalidateQueries('passengers')
      queryClient.invalidateQueries('passenger-stats')
      setSelectedPendingIds([])
      onDeletePendingClose()

      if (failed === 0) {
        toast({
          title: 'Pending KYC Accounts Deleted',
          description: `${deleted} account(s) were permanently removed without sending email.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'Cleanup Partially Completed',
          description: `${deleted} account(s) were deleted, ${failed} could not be removed.`,
          status: 'warning',
          duration: 6000,
          isClosable: true,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Deletion Failed',
        description: error?.response?.data?.error?.message || 'Failed to delete the selected pending KYC accounts.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsDeletingPending(false)
    }
  }

  const handleResendFailedCleanupEmails = async (
    notificationIds: string[],
    payload: { emailSubject: string; emailMessage: string }
  ) => {
    setIsResendingFailedEmails(true)
    try {
      const response = await cmsService.resendPendingKycCleanupEmails({
        notificationIds,
        ...payload,
      })
      const queued = response?.data?.queued || 0
      const failed = response?.data?.failed || 0

      queryClient.invalidateQueries('pending-kyc-cleanup-email-attempts')
      await refetchFailedCleanupEmails()

      toast({
        title: failed === 0 ? 'Failed Emails Queued' : 'Resend Partially Queued',
        description:
          failed === 0
            ? `${queued} email(s) were queued for resend successfully.`
            : `${queued} email(s) were queued, but ${failed} could not be queued.`,
        status: failed === 0 ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Resend Failed',
        description: error?.response?.data?.error?.message || 'Failed to resend the selected emails.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsResendingFailedEmails(false)
    }
  }

  const handleStatusUpdate = async (userId: string, newStatus: 'active' | 'inactive' | 'suspended') => {
    try {
      await cmsService.updatePassengerStatus(userId, newStatus)
      queryClient.invalidateQueries('passengers')
      queryClient.invalidateQueries('passenger-stats')
      toast({ title: 'Status Updated', description: 'User status updated successfully.', status: 'success', duration: 3000, isClosable: true })
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.error?.message || 'Failed to update status.', status: 'error', duration: 5000, isClosable: true })
    }
  }

  if (isLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={2}>
          <Heading size="xl" color="gray.900">Commuters & Users</Heading>
          <Text color="gray.600">
            Commuters are passenger accounts with at least one booking. The Users tab shows all passenger accounts, whether verified or still pending KYC.
          </Text>
          <Text color="gray.500" fontSize="sm">
            Pending-KYC cleanup defaults to accounts older than {PENDING_KYC_CLEANUP_MINIMUM_AGE_HOURS} hours. Current split: {stalePendingCount} eligible, {freshPendingCount} fresh.
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button leftIcon={<Download size={16} />} variant="outline" size="sm">
            Export
          </Button>
          {pendingCount > 0 && (
            <Button
              leftIcon={<Send size={16} />}
              colorScheme="orange"
              variant="outline"
              size="sm"
              onClick={handleEmailPending}
            >
              Email {pendingCount} Pending
            </Button>
          )}
          {selectedPendingIds.length > 0 && (
            <Button
              leftIcon={<Trash2 size={16} />}
              colorScheme="red"
              variant="solid"
              size="sm"
              onClick={onDeletePendingOpen}
              >
              Delete {selectedPendingIds.length} Pending
            </Button>
          )}
          {failedCleanupEmails.length > 0 && (
            <Button
              leftIcon={<Mail size={16} />}
              colorScheme="orange"
              variant="ghost"
              size="sm"
              onClick={onFailedEmailsOpen}
            >
              Failed Emails {failedCleanupEmails.length}
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
        {stats.map((stat, index) => (
          <Card key={index} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" lineHeight="1.2">
                    {stat.title}
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="gray.900">
                    {typeof stat.value === 'number' && stat.title.includes('Wallet')
                      ? `TZS ${Number(stat.value ?? 0).toLocaleString()}`
                      : (stat.value ?? 'N/A')}
                  </Text>
                </VStack>
                <Box p={2} borderRadius="lg" bg={`${stat.color}.100`} color={`${stat.color}.600`}>
                  <stat.icon size={20} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Tabs */}
      <Tabs colorScheme="brand" variant="enclosed-colored">
        <TabList>
          <Tab fontWeight="semibold">
            <HStack spacing={2}>
              <Ticket size={16} />
              <Text>Commuters</Text>
              <Badge colorScheme="purple" borderRadius="full" px={2}>{statsData.commuters || commuters.length}</Badge>
            </HStack>
          </Tab>
          <Tab fontWeight="semibold">
            <HStack spacing={2}>
              <Users size={16} />
              <Text>All Users</Text>
              <Badge colorScheme="blue" borderRadius="full" px={2}>{statsData.registered || statsData.total || registeredUsers.length}</Badge>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Commuters Tab */}
          <TabPanel px={0} pt={4}>
            <UserTable
              users={commuters}
              isCommuter={true}
              onView={(u) => handleView(u, true)}
              onEdit={handleEdit}
              onEmail={handleEmail}
              onStatusUpdate={handleStatusUpdate}
              searchTerm={commSearchTerm}
              setSearchTerm={setCommSearchTerm}
              selectedStatus={commStatus}
              setSelectedStatus={setCommStatus}
              selectedKycStatus={commKyc}
              setSelectedKycStatus={setCommKyc}
              paidOnly={commPaid}
              setPaidOnly={setCommPaid}
              minBookings={commMinBookings}
              setMinBookings={setCommMinBookings}
              dateRange={commDateRange}
              setDateRange={setCommDateRange}
              currentPage={commPage}
              setCurrentPage={setCommPage}
              itemsPerPage={ITEMS_PER_PAGE}
              selectedPendingIds={selectedPendingIds}
              onTogglePendingSelection={togglePendingSelection}
              onToggleAllPendingSelection={toggleAllPendingSelection}
              showForceDeleteCandidates={showForceDeleteCandidates}
              onShowForceDeleteCandidatesChange={setShowForceDeleteCandidates}
            />
          </TabPanel>

          {/* Registered Users Tab */}
          <TabPanel px={0} pt={4}>
            <UserTable
              users={registeredUsers}
              isCommuter={false}
              onView={(u) => handleView(u, false)}
              onEdit={handleEdit}
              onEmail={handleEmail}
              onStatusUpdate={handleStatusUpdate}
              searchTerm={userSearchTerm}
              setSearchTerm={setUserSearchTerm}
              selectedStatus={userStatus}
              setSelectedStatus={setUserStatus}
              selectedKycStatus={userKyc}
              setSelectedKycStatus={setUserKyc}
              paidOnly={userPaid}
              setPaidOnly={setUserPaid}
              minBookings={userMinBookings}
              setMinBookings={setUserMinBookings}
              dateRange={userDateRange}
              setDateRange={setUserDateRange}
              currentPage={userPage}
              setCurrentPage={setUserPage}
              itemsPerPage={ITEMS_PER_PAGE}
              selectedPendingIds={selectedPendingIds}
              onTogglePendingSelection={togglePendingSelection}
              onToggleAllPendingSelection={toggleAllPendingSelection}
              showForceDeleteCandidates={showForceDeleteCandidates}
              onShowForceDeleteCandidatesChange={setShowForceDeleteCandidates}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modals */}
      <CommuterViewModal isOpen={isViewOpen} onClose={onViewClose} commuter={selectedUser} isCommuter={isViewingCommuter} />
      <CommuterEditModal isOpen={isEditOpen} onClose={onEditClose} commuter={selectedUser} />
      <SendEmailModal isOpen={isEmailOpen} onClose={onEmailClose} commuters={emailTargets} />
      <PendingKycDeleteModal
        isOpen={isDeletePendingOpen}
        onClose={onDeletePendingClose}
        selectedUsers={selectedPendingUsers}
        onConfirm={handleDeletePendingKyc}
        isLoading={isDeletingPending}
      />
      <FailedCleanupEmailsModal
        isOpen={isFailedEmailsOpen}
        onClose={onFailedEmailsClose}
        attempts={failedCleanupEmails}
        isLoading={isResendingFailedEmails || isFetchingFailedCleanupEmails}
        onRefresh={() => { void refetchFailedCleanupEmails() }}
        onResend={handleResendFailedCleanupEmails}
      />
    </VStack>
  )
}
